import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from '../Button/Button';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import styles from './ConfirmModal.module.css';

export type ConfirmModalVariant = 'danger' | 'warning' | 'info' | 'success';
export type ConfirmModalAlignment = 'center' | 'left';

interface ConfirmModalProps {
  open: boolean;
  title: string;
  titleAction?: React.ReactNode;
  message?: string;
  children?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  hideCancel?: boolean;
  variant?: ConfirmModalVariant;
  icon?: string;
  confirmIcon?: string;
  loading?: boolean;
  loadingLabel?: string;
  confirmDelaySeconds?: number;
  onConfirm: () => void;
  onCancel: () => void;
  width?: number;
  alignment?: ConfirmModalAlignment;
}

const ICON_MAP: Record<ConfirmModalVariant, string> = {
  danger: 'delete_forever',
  warning: 'warning',
  info: 'info',
  success: 'check_circle',
};

const CONFIRM_VARIANT_MAP: Record<ConfirmModalVariant, 'danger' | 'primary'> = {
  danger: 'danger',
  warning: 'primary',
  info: 'primary',
  success: 'primary',
};

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  title,
  titleAction,
  message,
  children,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  hideCancel = false,
  variant = 'danger',
  icon,
  confirmIcon,
  loading = false,
  loadingLabel = 'Aguarde...',
  confirmDelaySeconds,
  onConfirm,
  onCancel,
  width,
  alignment = 'center',
}) => {
  const [confirmCountdown, setConfirmCountdown] = useState(0);

  useEscapeToClose(onCancel, open && !loading);

  useEffect(() => {
    if (!open) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !confirmDelaySeconds || confirmDelaySeconds <= 0) {
      setConfirmCountdown(0);
      return;
    }

    setConfirmCountdown(confirmDelaySeconds);

    const intervalId = window.setInterval(() => {
      setConfirmCountdown((current) => (current > 0 ? current - 1 : 0));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [confirmDelaySeconds, open]);

  if (!open) return null;

  const iconName = icon || ICON_MAP[variant];
  const isConfirmLocked = confirmCountdown > 0;

  return createPortal(
    <div className={styles.overlay}>
      <div
        className={`${styles.modal} ${alignment === 'left' ? styles['modal-left'] : ''}`}
        style={width == null
          ? undefined
          : { '--confirm-modal-width': `${width}px` } as React.CSSProperties}
      >
        <div className={`${styles['icon-circle']} ${styles[`icon-circle-${variant}`]}`}>
          <span className="material-symbols-outlined">{iconName}</span>
        </div>

        {titleAction ? (
          <div className={styles.titleRow}>
            <h3 className={styles.title}>{title}</h3>
            {titleAction}
          </div>
        ) : <h3 className={styles.title}>{title}</h3>}
        {message && <p className={styles.message}>{message}</p>}
        {children && <div className={styles.content}>{children}</div>}

        <div className={styles.actions}>
          {!hideCancel && (
            <Button
              type="button"
              variant="ghost"
              size="small"
              className={styles['btn-action']}
              onClick={onCancel}
              disabled={loading}
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            type="button"
            variant={CONFIRM_VARIANT_MAP[variant]}
            size="small"
            icon={loading ? (confirmIcon || undefined) : confirmIcon}
            className={[
              styles['btn-action'],
              loading && confirmIcon ? styles['btn-confirm-generating'] : '',
            ].filter(Boolean).join(' ')}
            onClick={onConfirm}
            disabled={loading || isConfirmLocked}
            aria-busy={loading}
          >
            {loading ? (
              loadingLabel
            ) : (
              <span className={styles['confirm-button-content']}>
                <span>{confirmLabel}</span>
                {isConfirmLocked && (
                  <span className={styles['confirm-countdown']} aria-hidden="true">
                    {confirmCountdown}
                  </span>
                )}
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ConfirmModal;
